import { useEffect } from 'react'
import useStore from '../../../store'
import { useParams } from 'react-router-dom'
import { useIsMobile } from '../../../hooks/useIsMobile'
import DealerInfo from './DealerInfo'
import DealerNameHeader from './DealerNameHeader'
import Spinner from '../../common/Spinner'
import DashboardContainer from '../../common/DashboardContainer'
import AmDealerDashboardSidePanel from './AmDealerDashboardSidePanel'
import UnitsNeedingAttention from './UnitsNeedingAttention'
import Breadcrumbs from '../../common/Breadcrumbs'
import SaveDealerIcon from '../Dealers/SavedDealers/SaveDealerIcon'
import { useNavigate } from 'react-router-dom'
import formatter from '../../../constants/currencyFormatter'
import { requestFloorAsAmEnabled } from '../../../utilities/featureFlags'

export default function AmDealerDashboard() {
  const isMobile = useIsMobile()
  const { dealerId } = useParams()
  const navigate = useNavigate()

  const dealer = useStore((state) => state.dealer)
  const fetchUnitsAsAm = useStore((state) => state.fetchUnitsAsAm)
  const dealerUnitsPage = useStore((state) => state.dealerUnitsPage)
  const fetchDealer = useStore((state) => state.fetchDealer)
  const fetchBankAccountsAsAm = useStore((state) => state.fetchBankAccountsAsAm)
  const amUnitsTableParams = useStore((state) => state.amUnitsTableParams)
  const setActionButton = useStore((state) => state.setActionButton)
  const breadcrumbTrail = useStore((state) => state.breadcrumbTrail)
  const setBreadcrumbTrail = useStore((state) => state.setBreadcrumbTrail)
  const setUnitRowStyle = useStore((state) => state.setUnitRowStyle)
  const unit = useStore((state) => state.unit)

  const unitsNeedingAttention = Object.values({
    untouched: dealer?.attributes['untouched-unit-count'],
    sold: dealer?.attributes['sold-unit-count'],
    repo: dealer?.attributes['repo-unit-count'],
    pastDue: dealer?.attributes['past-due-unit-count'],
  }).some(count => count > 0)

  useEffect(() => {
    if (dealerId) {
      fetchBankAccountsAsAm(dealerId)
    }
    setUnitRowStyle('lg')
  }, [dealerId])

  useEffect(() => {
    fetchDealer(dealerId)
  }, [dealerId, unit])

  useEffect(() => {
    fetchUnitsAsAm(dealerId, amUnitsTableParams)
  }, [amUnitsTableParams, dealerId, dealerUnitsPage])

  useEffect(() => {
    if (dealer?.attributes['active-status'] === 'Active' && requestFloorAsAmEnabled) {
      setActionButton({
        buttonText: 'Request Floor',
        altTextHeader: "Credit Available",
        altTextBody: dealer ? formatter.format(dealer.attributes['total-credit-remaining']) : null,
        onClick: handleClick,
      })
    }

    return () => setActionButton(null)
  }, [dealerId, dealer])

  const handleClick = () => {
    const newBreadcrumbTrail = [
      ...breadcrumbTrail,
      {
        label: "Request Floor",
        path: '/blackbook'
      }
    ]
    setBreadcrumbTrail(newBreadcrumbTrail)
    navigate(`/blackbook`)
  }

  const sidePanels = [
    {
      isExpanded: true,
      component: <AmDealerDashboardSidePanel dealerId={dealerId} />,
      isFullscreenable: false,
      isClosable: false,
    }
  ]

  return (
    <DashboardContainer sidePanels={sidePanels}>
      <Breadcrumbs/>
      {dealer?.id !== dealerId ?
        <div className="flex w-full justify-center">
          <Spinner />
        </div>
      :
        <>
          <div className="relative w-full flex items-center justify-center">
            <div className="absolute left-0 ml-2">
              <SaveDealerIcon dealer={dealer} />
            </div>
            <div className="text-center">
              <DealerNameHeader
                name={dealer?.attributes.name ? dealer.attributes.name : 'No Name'}
              />
            </div>
          </div>
          {unitsNeedingAttention && <UnitsNeedingAttention/>}
        </>
      }
      {isMobile ? <AmDealerDashboardSidePanel dealerId={dealerId} /> : <DealerInfo dealerId={dealerId}/>}
    </DashboardContainer>
  )
}
